import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { OPENAI_GPT_IMAGE_SIZE_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const GPT_IMAGE_2_ASPECT_RATIOS = [
  { label: "9:16", value: "9:16" },
  { label: "16:9", value: "16:9" },
  { label: "1:1", value: "1:1" },
] as const;

const GPT_IMAGE_2_QUALITY_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
] as const;

type GptImage2AspectRatio = (typeof GPT_IMAGE_2_ASPECT_RATIOS)[number]["value"];
type GptImage2Quality = (typeof GPT_IMAGE_2_QUALITY_OPTIONS)[number]["value"];

// Per-image pricing (USD) for gpt-image-2.
// https://developers.openai.com/api/docs/guides/image-generation
const COST_PER_IMAGE_USD: Record<
  GptImage2Quality,
  Record<GptImage2AspectRatio, number>
> = {
  low: { "1:1": 0.006, "16:9": 0.005, "9:16": 0.005 },
  medium: { "1:1": 0.053, "16:9": 0.041, "9:16": 0.041 },
  high: { "1:1": 0.211, "16:9": 0.165, "9:16": 0.165 },
  // "auto" billing is decided by OpenAI at request time. Charge the "high"
  // price up-front so the hold covers the worst case.
  auto: { "1:1": 0.211, "16:9": 0.165, "9:16": 0.165 },
};

// Runware needs explicit width/height; OpenAI direct uses the size string in
// OPENAI_GPT_IMAGE_SIZE_MAP. Same 3 aspect ratios, expressed two ways.
const DIMENSION_MAP: Record<
  GptImage2AspectRatio,
  { width: number; height: number }
> = {
  "1:1": { width: 1024, height: 1024 },
  "16:9": { width: 1536, height: 1024 },
  "9:16": { width: 1024, height: 1536 },
};

// /v1/images/edits accepts multiple reference images. Expose 4 slots.
const ATTACHMENT_SLOTS = Array.from({ length: 4 }, (_, index) => ({
  type: "image" as const,
  allowedFormats: ["image/jpeg", "image/png", "image/webp"],
  label: `Image ${index + 1}`,
  slot: `reference_image_${index + 1}`,
  group: "reference",
  required: false,
}));

const gptImage2Params = z
  .object({
    aspectRatio: z.enum(["1:1", "16:9", "9:16"] as const).default("9:16"),
    quality: z.enum(["auto", "high", "medium", "low"] as const).default("auto"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const gptImage2 = defineModel({
  key: "gpt-image-2",
  stepType: "image",
  spec: {
    label: "GPT Image 2",
    description:
      "OpenAI's latest image model with flexible sizes, high-fidelity edits, and excellent text rendering",
    icon: "openai.svg",
    tags: ["image-gen", "image-edit"],
    featured: true,
    features: {
      aspectRatio: true,
      quality: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: GPT_IMAGE_2_ASPECT_RATIOS },
      quality: { items: GPT_IMAGE_2_QUALITY_OPTIONS },
      attachments: ATTACHMENT_SLOTS,
    },
    params: gptImage2Params,
  },
  referencePricing: {
    provider: "OpenAI",
    url: "https://openai.com/api/pricing",
    entries: [
      { unit: "per_image", unitPriceMicrodollars: 5_000, variant: "Low" },
      { unit: "per_image", unitPriceMicrodollars: 41_000, variant: "Medium" },
      { unit: "per_image", unitPriceMicrodollars: 165_000, variant: "High" },
    ],
  },
  providers: [
    // Runware first (default): webhook async dispatch removes the 2-min sync
    // hold of the OpenAI direct path. Runware proxies to OpenAI under the hood
    // at passthrough rates, so cost matches the OpenAI binding below.
    {
      provider: "runware",
      modelId: "openai:gpt-image@2",
      estimateCost(params) {
        const aspectRatio = params.aspectRatio ?? "9:16";
        const quality = params.quality ?? "auto";
        const costUsd = COST_PER_IMAGE_USD[quality][aspectRatio];
        return Math.ceil(costUsd * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params) {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const referenceImages =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        const dimensions = DIMENSION_MAP[params.aspectRatio];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          width: dimensions.width,
          height: dimensions.height,
          outputFormat: "PNG",
          ...(referenceImages.length > 0 ? { referenceImages } : {}),
          providerSettings: {
            openai: {
              quality: params.quality,
            },
          },
        };
      },
    },
    // OpenAI direct (fallback): used when Runware is unavailable. See
    // packages/backend-utils/src/openai-images.ts for the runtime client.
    {
      provider: "openai",
      modelId: "gpt-image-2",
      estimateCost(params) {
        const aspectRatio = params.aspectRatio ?? "9:16";
        const quality = params.quality ?? "auto";
        const costUsd = COST_PER_IMAGE_USD[quality][aspectRatio];
        return Math.ceil(costUsd * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params) {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const referenceImages =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        return {
          model: this.modelId,
          prompt: params.prompt,
          size: OPENAI_GPT_IMAGE_SIZE_MAP[params.aspectRatio],
          quality: params.quality,
          output_format: "png",
          ...(referenceImages.length > 0 ? { referenceImages } : {}),
        };
      },
    },
  ],
});
